import { useEffect, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AdminRoute } from '@/components/AdminRoute';

// Eagerly load Landing to eliminate modulepreload chain on first visit
import Landing from './pages/Landing';

const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tasks = lazy(() => import('./pages/Tasks'));
const WorkLog = lazy(() => import('./pages/WorkLog'));
const Applications = lazy(() => import('./pages/Applications'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const Meetings = lazy(() => import('./pages/Meetings'));
const Skills = lazy(() => import('./pages/Skills'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const Diary = lazy(() => import('./pages/Diary'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Changelog = lazy(() => import('./pages/Changelog'));
const Install = lazy(() => import('./pages/Install'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ReleasesPage = lazy(() => import('./pages/admin/ReleasesPage'));
const FeatureFlagsPage = lazy(() => import('./pages/admin/FeatureFlagsPage'));
const BetaUsersPage = lazy(() => import('./pages/admin/BetaUsersPage'));
const AccessRequestsPage = lazy(() => import('./pages/admin/AccessRequestsPage'));
const AdminGuidePage = lazy(() => import('./pages/admin/AdminGuidePage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setSession, setLoading } = useAuthStore();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [setSession, setLoading]);

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                  <Route path="/tasks" element={<ErrorBoundary><Tasks /></ErrorBoundary>} />
                  <Route path="/worklog" element={<ErrorBoundary><WorkLog /></ErrorBoundary>} />
                  <Route path="/applications" element={<ErrorBoundary><Applications /></ErrorBoundary>} />
                  <Route path="/calendar" element={<ErrorBoundary><CalendarPage /></ErrorBoundary>} />
                  <Route path="/meetings" element={<ErrorBoundary><Meetings /></ErrorBoundary>} />
                  <Route path="/skills" element={<ErrorBoundary><Skills /></ErrorBoundary>} />
                  <Route path="/diary" element={<ErrorBoundary><Diary /></ErrorBoundary>} />
                  <Route path="/profile" element={<ErrorBoundary><ProfilePage /></ErrorBoundary>} />
                  <Route path="/settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
                </Route>
                <Route element={<AdminRoute><AdminDashboard /></AdminRoute>}>
                  <Route path="/admin" element={<ReleasesPage />} />
                  <Route path="/admin/features" element={<FeatureFlagsPage />} />
                  <Route path="/admin/beta-users" element={<BetaUsersPage />} />
                  <Route path="/admin/access-requests" element={<AccessRequestsPage />} />
                  <Route path="/admin/guide" element={<AdminGuidePage />} />
                </Route>
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/install" element={<Install />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;